import { Flex, Group, Stack, Text, Tooltip } from '@mantine/core';
import { useHover } from '@mantine/hooks';
import useRunId from '@/hooks/useRunId.ts';
import { useMatch, useNavigate } from 'react-router-dom';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { formatCalendarStyle } from '@/utils/datetime.ts';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import RunMenu, { RUN_MENU_TRIGGER_SIZE } from '@/components/RunMenu.tsx';
import { EditableText } from '@/components/EditableText.tsx';
import React, { useState } from 'react';
import useUpdateRun from '@/hooks/api/runs/useUpdateRun.ts';
import { getRunDisplayName, Run } from '@/types/runs.ts';
import RunTypeBadge from '@/components/RunTypeBadge.tsx';

type props = {
  run: Run;
  showMenu?: boolean;
};

const RunHistoryCard = ({ run, showMenu = true }: props) => {
  const { hovered, ref } = useHover();
  const runId = useRunId();
  const automationId = useAutomationId();
  const navigate = useNavigate();

  const bg = run.id === runId || hovered ? '#FFEAF7' : 'transparent';

  const [renamingRun, setRenamingRun] = useState(false);

  const { mutate: updateRun, isPending: isUpdatingRun } = useUpdateRun(run.id);

  const isRunPage = !!useMatch('/automations/:automationId/runs/*');
  const runDisplayName = getRunDisplayName(run);
  const navigateToRun = () => {
    if (isRunPage) {
      navigate(`/automations/${automationId}/runs/${run.id}`, { replace: true });
    } else {
      navigate(`/automations/${automationId}/results/${run.id}`, { replace: true });
    }
  };
  const handleNavigateKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    navigateToRun();
  };

  return (
    <Group
      gap={'2px'}
      align={'flex-start'}
      style={{
        backgroundColor: bg,
        borderRadius: '5px',
      }}
      ref={ref}
      p={'xs'}
      py={'6px'}
      wrap={'nowrap'}
      // h={'40px'}
    >
      <Flex pt={2}>
        <StatusIndicator status={run.status} include_text={false} />
      </Flex>
      {!renamingRun && !isUpdatingRun ? (
        <Stack gap={3} w={'100%'} style={{ minWidth: 0 }}>
          <Flex
            align="center"
            style={{
              minWidth: 0,
              position: 'relative',
              paddingRight: showMenu ? `${RUN_MENU_TRIGGER_SIZE}px` : undefined,
            }}
          >
            <Tooltip label={runDisplayName} position={'right'} openDelay={250}>
              <Text
                fw={500}
                lineClamp={1}
                size={'sm'}
                style={{ wordBreak: 'break-all', flex: 1, minWidth: 0, cursor: 'pointer' }}
                onClick={navigateToRun}
                onKeyDown={handleNavigateKeyDown}
                tabIndex={0}
                role="link"
              >
                {runDisplayName}
              </Text>
            </Tooltip>
            {showMenu ? (
              <Flex
                w={RUN_MENU_TRIGGER_SIZE}
                h={RUN_MENU_TRIGGER_SIZE}
                align="center"
                justify="center"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <RunMenu run={run} visible={hovered} onRename={() => setRenamingRun(true)}>
                  <span
                    aria-hidden="true"
                    data-testid="run-history-menu-slot"
                    style={{
                      display: 'block',
                      width: RUN_MENU_TRIGGER_SIZE,
                      height: RUN_MENU_TRIGGER_SIZE,
                    }}
                  />
                </RunMenu>
              </Flex>
            ) : null}
          </Flex>
          <Text
            size="xs"
            c={'dimmed'}
            truncate="end"
            lh={1.2}
            style={{ cursor: 'pointer' }}
            onClick={navigateToRun}
            onKeyDown={handleNavigateKeyDown}
            tabIndex={0}
            role="link"
            aria-label={`${runDisplayName} timestamp`}
          >
            {formatCalendarStyle(run.created_at)}
          </Text>
          <Group
            gap={4}
            wrap="wrap"
            style={{ minWidth: 0, cursor: 'pointer' }}
            onClick={navigateToRun}
            onKeyDown={handleNavigateKeyDown}
            tabIndex={0}
            role="link"
            aria-label={`${runDisplayName} metadata`}
          >
            <RunTypeBadge run={run} />
          </Group>
        </Stack>
      ) : (
        <EditableText
          name={run.name}
          onSave={(t) => updateRun({ name: t })}
          onLooseFocus={() => setRenamingRun(false)}
          saving={isUpdatingRun}
          size={'sm'}
          placeholder={'Name'}
          editing={true}
        />
      )}
    </Group>
  );
};

export default RunHistoryCard;
